import {useEffect, useMemo, useState} from 'react';
import {Box, Center, Flex, Stack} from '@chakra-ui/react';
import {useShallow} from 'zustand/react/shallow';
import {RoomContext} from '@livekit/components-react';
import {useChatScroll, useScreenSize, Loader} from 'ui-kit';
import {api, hasValue, useT, useUploadQueue} from '@kcast-fe/core';
import {useMessagesStore} from 'stores';
import {useLiveKit} from 'core';

import {
  MessageForm,
  MessageList,
  ThreadPopup,
  DocumentsButton,
  ScrollButton,
  VoiceControl,
  StartVoiceButton,
  AddDocumentButton,
  ContextDocuments
} from './components';

const WIDTH = {base: '920px', smToMd: '100%'};
const FORM_WIDTH = {base: '928px', smToMd: '100%'};

const LiveChatSceneFC = () => {
  const {data: threads} = api.threads.useThreadsQuery();
  const {data: documents} = api.documents.useDocumentsQuery();
  const documentUpload = api.documents.useUploadContextDocumentMutation();

  const {
    filter,
    chatKey,
    isThinking,
    isConnecting,
    isReconnecting,
    isNotFinalMsgExists,
    wsActions,
    actions
  } = useMessagesStore(useShallow((state) => state));

  const {t} = useT();
  const lk = useLiveKit();

  const {isLessThan768} = useScreenSize();
  const {vRef, cRef, isScrolledToEnd, scrollToBottom} = useChatScroll();

  const [lastVisibleIdx, setLastVisibleIdx] = useState(0);

  const {queue, documentsQueue, hasInProgress, queueActions} = useUploadQueue({
    upload: documentUpload
  });

  const isSendingDisabled = useMemo(
    () => isThinking || isNotFinalMsgExists || hasInProgress,
    [isThinking, isNotFinalMsgExists, hasInProgress]
  );

  useEffect(() => {
    actions.subscribe().then();

    return () => {
      actions.reset();
    };
  }, [actions]);

  useEffect(() => {
    if (isThinking) {
      setTimeout(() => {
        scrollToBottom('smooth');
      }, 150);
    }
  }, [isThinking, scrollToBottom]);

  const isVoice = useMemo(() => lk.isConnected || lk.isPending, [lk.isConnected, lk.isPending]);

  const currentThreadName = useMemo(() => {
    return hasValue(threads) && hasValue(filter) ? threads.get(filter.id) : undefined;
  }, [threads, filter]);

  const documentsAmount = useMemo(() => {
    return documents?.docs ? documents.docs.length : undefined;
  }, [documents]);

  return (
    <Flex h={'100%'} flexDirection={'column'} position={'relative'}>
      {isLessThan768 && (
        <>
          <Box
            pos={'absolute'}
            top={0}
            left={0}
            right={0}
            h="54px"
            bgGradient="to-b"
            gradientFrom="{colors.bgMain}"
            gradientTo="transparent"
            zIndex={1}
          />
          <Box
            pos={'absolute'}
            top={'4px'}
            left={'12px'}
            right={'12px'}
            bg={'{colors.white}'}
            border={'thin solid'}
            borderColor={'{colors.borderDefault}'}
            borderRadius={'full'}
            zIndex={10}
          >
            <Flex justifyContent={'end'} borderRadius={'24px'} h={'54px'}>
              <Flex pr={'10px'} h={'54px'} alignItems={'center'} justifyContent={'flex-end'}>
                <DocumentsButton label={documentsAmount} />
              </Flex>
            </Flex>
          </Box>
        </>
      )}

      <Flex
        minH={'1px'}
        flex={'1 1 auto'}
        overflow={'hidden'}
        flexDirection={'column'}
        position={'relative'}
      >
        <Stack gap={'0'} h={'100%'}>
          <Flex ref={cRef} flex={'1 1 auto'} overflowY={'auto'} flexDirection={'column'}>
            {isReconnecting && (
              <Box h={'100%'}>
                <Loader isCenter label={t('labels.reconnecting')} />
              </Box>
            )}

            {/* It prevents animation on first loading */}
            {hasValue(chatKey) && !isReconnecting && (
              <Center>
                <Box w={WIDTH}>
                  <MessageList cRef={cRef} vRef={vRef} onLastVisibleIdx={setLastVisibleIdx} />
                </Box>
              </Center>
            )}
          </Flex>

          <Box
            position={'absolute'}
            bottom={isVoice || isLessThan768 ? '0' : '12px'}
            zIndex={5}
            right={0}
            left={0}
          >
            <Center w={'100%'} className={'standalonePb'}>
              <Box w={'100%'} maxW={FORM_WIDTH} p={{base: '0 70px', smToMd: '0 16px'}}>
                {isVoice ? (
                  /* VOICE CONTROLS */
                  <RoomContext.Provider value={lk.room}>
                    <VoiceControl lk={lk} cRef={cRef} />
                  </RoomContext.Provider>
                ) : (
                  /* MSG FORM WRAPPER */
                  <Box w={'100%'} position={'relative'}>
                    {/* CONTEXT DOCUMENTS */}
                    <ContextDocuments
                      queue={queue}
                      documentsQueue={documentsQueue}
                      hasThread={hasValue(currentThreadName)}
                    />

                    {/* FORM POPUP */}
                    <ThreadPopup threadName={currentThreadName} lastVisibleIdx={lastVisibleIdx} />

                    {/* MSG FORM */}
                    <MessageForm
                      isDisabled={isConnecting}
                      isSendingDisabled={isSendingDisabled}
                      tryToSendMsg={wsActions.sendTextMessage}
                      documentSlot={
                        <AddDocumentButton
                          queueActions={queueActions}
                          documentsQueue={documentsQueue}
                          isDisabled={isConnecting || isSendingDisabled}
                        />
                      }
                      voiceSlot={
                        <StartVoiceButton lk={lk} isDisabled={isConnecting || isSendingDisabled} />
                      }
                    />

                    {/* RIGHT BUTTONS */}
                    {!isLessThan768 && (
                      <Box position={'absolute'} bottom={'14px'} right={'-50px'}>
                        <DocumentsButton label={documentsAmount} />
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            </Center>

            {/* SCROLL TO BOTTOM */}
            {!isScrolledToEnd && <ScrollButton onScrollClick={() => scrollToBottom('smooth')} />}
          </Box>
        </Stack>
      </Flex>
    </Flex>
  );
};

export const LiveChatScene = LiveChatSceneFC;
